import { useEffect, useState } from "react";
import { ChevronDown, Download, Loader2, Mail, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

const blobError = async (e) => {
  const d = e?.response?.data;
  if (d instanceof Blob) { try { return JSON.parse(await d.text()).detail || apiError(e); } catch { return apiError(e); } }
  return apiError(e);
};

const waPhone = (v) => {
  let d = (v || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("90")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d.length === 10 && d.startsWith("5") ? `90${d}` : null;
};

async function downloadPdf(id, tpl, channel = "indir", to = "") {
  const res = await api.get(`/orders/${id}/doc.pdf`, { params: { template_id: tpl, channel, to }, responseType: "blob" });
  const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "belge.pdf";
  const url = URL.createObjectURL(res.data);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function TemplatePick({ info, value, onChange, testid = "share-template-select" }) {
  return (
    <select className="h-8 px-2 rounded-md border border-slate-200 bg-white text-[13px] max-w-[260px]" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} title="Paylaşılacak şablon">
      {info.templates.map((t) => <option key={t.id} value={t.id}>{t.doc_type_name} · {t.name}{t.is_default ? " (varsayılan)" : ""}</option>)}
    </select>
  );
}

function EmailDialog({ id, info, tpl, setTpl, onClose }) {
  const opts = [...new Set(info.allowed_recipients)];
  const [to, setTo] = useState(info.email || opts[0] || "");
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => `inv-mail-${id}-${Date.now()}`);
  const cur = info.templates.find((t) => t.id === tpl);
  const send = async () => {
    if (!opts.includes(to)) return toast.error("Listeden bir alıcı seçin");
    setBusy(true);
    try { await api.post(`/orders/${id}/doc/email`, { template_id: tpl, to: to.trim() }, { headers: { "Idempotency-Key": `${key}-${tpl}` } }); toast.success(`${cur?.doc_no} e-posta ile gönderildi`); onClose(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="invoice-email-dialog">
        <DialogHeader><DialogTitle>E-posta ile Gönder · {cur?.doc_no}</DialogTitle></DialogHeader>
        {!info.email_enabled && <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="invoice-email-disabled">E-posta gönderimi kapalı (EMAIL_SENDING_ENABLED)</div>}
        <label className="text-[12px] text-slate-500">Şablon</label>
        <TemplatePick info={info} value={tpl} onChange={setTpl} testid="invoice-email-template" />
        <label className="text-[12px] text-slate-500">Alıcı</label>
        {!info.email && <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="invoice-email-no-cari-email">Bu carinin kayıtlı e-postası yok. Cari kartından e-posta ekleyin.</div>}
        <select className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm w-full" value={to} onChange={(e) => setTo(e.target.value)} data-testid="invoice-email-to">
          {opts.length === 0 && <option value="">Alıcı yok</option>}
          {opts.map((r) => <option key={r} value={r}>{r === info.email ? `${r} (cari)` : `${r} (şirket içi)`}</option>)}
        </select>
        <p className="text-[11px] text-slate-500">Yalnızca carinin kayıtlı e-postası veya şirket içi alıcılar. PDF ek olarak gönderilir; içerik sabit şablondur.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button disabled={busy || !info.email_enabled} onClick={send} className="bg-blue-600 hover:bg-blue-700" data-testid="invoice-email-send">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}Gönder</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhoneDialog({ info, tpl, setTpl, onSend, onClose }) {
  const [v, setV] = useState(info.whatsapp_phone ? `0${info.whatsapp_phone.slice(2)}` : "");
  const ok = waPhone(v);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="invoice-wa-dialog">
        <DialogHeader><DialogTitle>WhatsApp ile Gönder</DialogTitle></DialogHeader>
        <label className="text-[12px] text-slate-500">Şablon</label>
        <TemplatePick info={info} value={tpl} onChange={setTpl} testid="invoice-wa-template" />
        {!info.whatsapp_phone && <p className="text-[12px] text-slate-500">Carinin geçerli cep telefonu yok. Numarayı girin (05XX XXX XX XX).</p>}
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="0532 123 45 67" data-testid="invoice-wa-phone" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button disabled={!ok} onClick={() => onSend(ok)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="invoice-wa-send"><MessageCircle className="h-4 w-4 mr-1" />Gönder</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PdfDialog({ info, tpl, setTpl, onDownload, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="invoice-pdf-dialog">
        <DialogHeader><DialogTitle>PDF İndir</DialogTitle></DialogHeader>
        <label className="text-[12px] text-slate-500">Şablon</label>
        <TemplatePick info={info} value={tpl} onChange={setTpl} testid="invoice-pdf-template" />
        <p className="text-[11px] text-slate-500">PDF sunucuda seçilen şablondan üretilir; baskı sayısına eklenmez.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button onClick={onDownload} className="bg-slate-900" data-testid="invoice-pdf-confirm"><Download className="h-4 w-4 mr-1" />İndir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Paylaşım: PDF İndir / E-posta / WhatsApp — her diyalogda şablon seçimi (varsayılan: türün varsayılanı ya da `defaultTemplate`).
// menu=true: tek "Paylaş ▾" düğmesi; her seçenek kendi şablon seçimli diyaloğunu açar (sipariş detayı araç çubuğu).
export default function InvoiceShare({ id, defaultTemplate, className = "", menu = false }) {
  const [info, setInfo] = useState(null);
  const [tpl, setTpl] = useState("");
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get(`/orders/${id}/invoice/share-info`).then(({ data }) => {
      setInfo(data);
      setTpl(data.templates.some((t) => t.id === defaultTemplate) ? defaultTemplate : data.default_template_id);
    }).catch(() => setInfo(false));
  }, [id, defaultTemplate]);
  if (!info) return null;
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(await blobError(e)); } setBusy(""); };
  const whatsapp = async (phone) => {
    const w = window.open("", "_blank");
    try {
      await downloadPdf(id, tpl, "whatsapp", phone);
      const text = info.templates.find((t) => t.id === tpl)?.whatsapp_text || info.whatsapp_text;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
      if (w) w.location.href = url; else window.open(url, "_blank");
      toast.info("PDF indirildi, WhatsApp'ta dosyayı ekleyin", { duration: 8000 });
    } catch (e) { w?.close(); throw e; }
  };
  const icon = (k, I) => (busy === k ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <I className="h-4 w-4 mr-1" />);
  const p = { info, tpl, setTpl, onClose: () => setDlg(null) };
  const dialogs = (<>
    {dlg === "pdf" && <PdfDialog {...p} onDownload={() => { setDlg(null); run("pdf", () => downloadPdf(id, tpl)); }} />}
    {dlg === "email" && <EmailDialog id={id} {...p} />}
    {dlg === "wa" && <PhoneDialog {...p} onSend={(ph) => { setDlg(null); run("wa", () => whatsapp(ph)); }} />}
  </>);
  if (menu) return (
    <div className="print:hidden" data-testid="invoice-share">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild><Button size="sm" variant="outline" disabled={!!busy} data-testid="share-menu-button">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Share2 className="h-4 w-4 mr-1" />}Paylaş<ChevronDown className="h-3.5 w-3.5 ml-1" /></Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="share-menu">
          <DropdownMenuItem onSelect={() => setDlg("pdf")} data-testid="share-menu-pdf"><Download className="h-4 w-4 mr-2" />PDF İndir</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDlg("email")} data-testid="share-menu-email"><Mail className="h-4 w-4 mr-2" />E-posta Gönder</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDlg("wa")} className="text-emerald-700" data-testid="share-menu-whatsapp"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp ile Paylaş</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </div>
  );
  return (
    <div className={`flex gap-2 items-center print:hidden ${className}`} data-testid="invoice-share">
      <TemplatePick info={info} value={tpl} onChange={setTpl} />
      <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("pdf", () => downloadPdf(id, tpl))} data-testid="invoice-pdf-download-button">{icon("pdf", Download)}PDF İndir</Button>
      <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setDlg("email")} data-testid="invoice-email-button">{icon("email", Mail)}E-posta ile Gönder</Button>
      <Button size="sm" variant="outline" disabled={!!busy} className="text-emerald-700 border-emerald-300" onClick={() => setDlg("wa")} data-testid="invoice-whatsapp-button">{icon("wa", MessageCircle)}WhatsApp ile Gönder</Button>
      {dialogs}
    </div>
  );
}
